<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import {
    AuthenticationRequestError,
    authRequest,
    type AuthenticationPurpose,
    type AuthenticationResult,
} from '../api.ts';
import { useAuthenticationAction } from '../authenticationAction.ts';

const props = defineProps<{
    challenge: string;
    purpose: AuthenticationPurpose;
    completing: boolean;
    resolve: (result: AuthenticationResult) => Promise<void>;
}>();
const emit = defineEmits<{
    back: [];
    expired: [title: string, detail: string];
}>();
const codeInput = ref<HTMLInputElement>();
const code = ref('');
const recovery = ref(false);
const { busy, run } = useAuthenticationAction();
const loading = computed(() => busy.value || props.completing);

onMounted(() => codeInput.value?.focus());

function useRecoveryCode() {
    recovery.value = !recovery.value;
    code.value = '';
}

function verifyTwoFactor() {
    void run(
        async () => {
            await props.resolve(
                await authRequest<AuthenticationResult>('two-factor', 'POST', {
                    challenge: props.challenge,
                    code: code.value,
                }),
            );
        },
        (reason) => {
            if (
                reason instanceof AuthenticationRequestError &&
                reason.response.status === 422 &&
                reason.message.toLowerCase().includes('invalid or has expired')
            ) {
                emit('expired', 'Authentication Expired', 'Sign in again to continue.');
                return true;
            }
        },
    );
}
</script>

<template>
    <form class="stack gap-lg" @submit.prevent="verifyTwoFactor">
        <div class="stack align-center gap-md text-center">
            <h2>{{ purpose === 'confirm' ? 'Confirm It’s You' : 'Sign In' }}</h2>
        </div>
        <p class="content">
            {{
                recovery
                    ? 'Please enter one of your emergency recovery codes.'
                    : 'Please enter the verification code from your authenticator app.'
            }}
        </p>
        <div class="stack gap-xs">
            <div class="row justify-between field-label">
                <label for="auth-code">{{ recovery ? 'Enter Recovery Code' : 'Enter Code' }}</label>
                <button type="button" class="link" :disabled="loading" @click="useRecoveryCode">
                    {{ recovery ? 'Use Authenticator Code' : 'Use a Recovery Code' }}
                </button>
            </div>
            <input
                id="auth-code"
                ref="codeInput"
                v-model="code"
                class="input text-lg"
                :autocomplete="recovery ? 'off' : 'one-time-code'"
                :inputmode="recovery ? undefined : 'numeric'"
                :disabled="loading"
                autocapitalize="none"
                spellcheck="false"
                required
            />
        </div>
        <button
            class="btn btn--lg bg-primary full-width"
            :class="{ 'is-loading': loading }"
            :disabled="loading"
        >
            Verify
        </button>
        <button
            type="button"
            class="btn btn--transparent color-primary full-width"
            :disabled="loading"
            @click="emit('back')"
        >
            {{ purpose === 'confirm' ? 'Use Another Method' : 'Back to Sign In' }}
        </button>
    </form>
</template>
